import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@cthings.co/styled-components';
import { Text, TextType } from '@bit/first-scope.text';
import { useWindowSize } from '@cthings.co/styles-utils';
import { Cheap } from '../../../../containers/common/reports/components/cheap/Cheap';
import { SelectListPopup } from './components/selectListPopup/SelectListPopup';
import { InputWrapper } from '../inputWrapper/InputWrapper';
import { Loader } from '../../../../containers/common/management/devices/components/loader/Loader';
import { useTheme } from '@cthings.co/styled-components';

import { colorFetchFDS as colorFetch } from '@cthings.co/styles-utils';

type FieldWrapperProps = {
  title?: string;
  isFieldScrollable: boolean;
  isEditInProcess?: boolean;
  isOpenSelectList?: boolean;
  isError?: boolean;
  isDisabled?: boolean;
};

const FieldWrapper = styled.div<FieldWrapperProps>`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: ${({ title, isFieldScrollable, isEditInProcess }) =>
    title
      ? '2px 4px 8px 12px'
      : isFieldScrollable
      ? '2px 40px 2px 6px'
      : isEditInProcess
      ? '2px 40px 0 6px'
      : '2px 40px 0 0'};
  border: ${({ isEditInProcess, title }) => (!isEditInProcess && !title ? 'none' : '1px solid')};
  border-color: ${({ isOpenSelectList, isError, isDisabled, theme }) =>
    isOpenSelectList
      ? colorFetch('primary')({ theme })
      : isError
      ? colorFetch('red2')({ theme })
      : isDisabled
      ? colorFetch('gray5')({ theme })
      : colorFetch('gray3')({ theme })};
  border-radius: ${({ theme }) => theme.borderRadius.primary};
  background-color: ${({ isDisabled, theme }) => (isDisabled ? colorFetch('gray5')({ theme }) : 'transparent')};
  transition: all 0.3s ease;
  overflow: hidden;
  cursor: ${({ isEditInProcess, title }) => (!isEditInProcess && !title ? 'auto' : 'pointer')};
  pointer-events: ${({ isEditInProcess, title, isDisabled }) =>
    (!isEditInProcess && !title) || isDisabled ? 'none' : 'auto'};
`;

type FieldProps = {
  fieldHeight: number;
};

const Field = styled.div<FieldProps>`
  width: 100%;
  height: ${({ fieldHeight }) => (fieldHeight < 116 ? 'max-content' : '100%')};
  max-height: 112px;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  overflow-y: ${({ fieldHeight }) => (fieldHeight < 116 ? 'hidden' : 'scroll')};
  &::-webkit-scrollbar {
    width: 4px;
    background: ${colorFetch('gray4')};
    opacity: 1;
    border-radius: ${({ theme }) => theme.borderRadius.primary};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${colorFetch('gray2')};
    border-radius: ${({ theme }) => theme.borderRadius.primary};
    position: relative;
    top: 4px;
    right: 4px;
  }
`;

const FieldText = styled(Text)`
  transition: none;
`;

const StyledLoader = styled(Loader)`
  right: 12px;
  top: 50%;
  position: absolute;
`;

export interface DropdownMultiChoiceOptions {
  placeholder: string;
  disabledPlaceholder?: string;
  title?: string;
  isValidatingStarted?: boolean;
  isError?: boolean;
  isOpenSelectList?: boolean;
  isSelectAll?: boolean;
  selectedIndexOfPeriod: number;
  popUpScrollTo: number;
  popUpScrollToWithCustomPeriod: number;
  wrapperRef: any;
  isDisabled?: boolean;
  paramsIsUploading?: boolean;
}
export interface DropdownMultiChoiceFunctions {
  handleCheckedItem: (id: string) => void;
  handleRemoveSelectedItem: (item: any) => void;
  handleSelectAll: () => void;
  setIsOpenSelectList: (value?: boolean) => void;
  setIsScrollableModal: any;
  handleTransformName?: (name: any) => string;
}

export const DropdownMultiChoice = ({
  parameters,
  selectedParameters,
  options,
  functions,
  isEditInProcess,
  languageStrings,
  ...props
}: {
  parameters: any[];
  selectedParameters: any[];
  options: DropdownMultiChoiceOptions;
  functions: DropdownMultiChoiceFunctions;
  isEditInProcess?: boolean;
  languageStrings?: any;
  className?: string;
}) => {
  const theme = useTheme();
  const { gray1, gray2, red2, primary } = theme.colors;

  const {
    placeholder,
    disabledPlaceholder,
    title,
    isValidatingStarted,
    isError,
    isOpenSelectList,
    isSelectAll,
    selectedIndexOfPeriod,
    popUpScrollTo,
    popUpScrollToWithCustomPeriod,
    wrapperRef,
    isDisabled,
    paramsIsUploading,
  } = options;
  const {
    handleCheckedItem,
    handleRemoveSelectedItem,
    handleSelectAll,
    setIsOpenSelectList,
    setIsScrollableModal,
    handleTransformName,
  } = functions;

  const [fieldHeight, setFieldHeight] = useState(40);

  const popupRef = useRef<any>(null);
  const screenWidth = useWindowSize()[0];
  const isShowMessage =
    screenWidth > 550 ? isValidatingStarted && isError : !isOpenSelectList && isValidatingStarted && isError;

  const openSelectList = useCallback(() => {
    setIsOpenSelectList(true);
    setIsScrollableModal(true);
  }, []);
  const closeSelectList = useCallback(() => {
    setIsOpenSelectList(false);
  }, []);
  const outsideHandler = useCallback(
    (e: any) => {
      if (isOpenSelectList && !(popupRef && popupRef.current && (popupRef as any).current.contains(e.target))) {
        closeSelectList();
      }
    },
    [isOpenSelectList, closeSelectList],
  );
  const fieldWrapperOnClick = (e: any) => {
    e.stopPropagation();
    !isDisabled && (isOpenSelectList ? closeSelectList() : openSelectList());
  };

  useEffect(() => {
    window.addEventListener('click', outsideHandler);
    return () => {
      window.removeEventListener('click', outsideHandler);
    };
  }, [outsideHandler]);

  useEffect(() => {
    setFieldHeight(fieldHeight);
  }, [fieldHeight]);

  useEffect(() => {
    if (isDisabled) {
      setIsOpenSelectList(false);
    }
  }, [isDisabled]);

  return (
    <InputWrapper title={title} isEditInProcess={isEditInProcess} {...props}>
      <FieldWrapper
        ref={(el: any) => {
          if (el) {
            setFieldHeight((val) => (val = el.getBoundingClientRect().height));
          }
        }}
        theme={theme}
        isOpenSelectList={isOpenSelectList}
        isError={isError && isValidatingStarted}
        onClick={fieldWrapperOnClick}
        title={title}
        isFieldScrollable={fieldHeight > 115}
        isEditInProcess={isEditInProcess}
        isDisabled={isDisabled}
      >
        <Field fieldHeight={fieldHeight}>
          {selectedParameters && selectedParameters.length > 0 ? (
            selectedParameters.map((item: any, index: number) => {
              const { name } = item;
              const value = handleTransformName ? handleTransformName(name) : name;
              return (
                <Cheap
                  key={index}
                  value={value}
                  closeOnClick={() => {
                    handleRemoveSelectedItem(item);
                  }}
                  isFieldGrowed={fieldHeight > 40}
                  isFieldScrollable={fieldHeight > 115}
                  isLastSelectedCheap={selectedParameters.length - 1 === index}
                  isEditInProcess={isEditInProcess}
                  title={title}
                />
              );
            })
          ) : (
            <FieldText
              type={TextType.TEXT_14_GRAY}
              color={!isEditInProcess && !title ? gray1 : gray2}
              weight={!isEditInProcess && !title ? '500' : '400'}
              margin={title ? '6px 0 0 0' : '2px 0 4px 0'}
              lineHeight={'22px'}
            >
              {!isEditInProcess && !title ? 'N/A' : isDisabled ? disabledPlaceholder : placeholder}
            </FieldText>
          )}
        </Field>
        {isOpenSelectList && !isDisabled && (
          <SelectListPopup
            popupRef={popupRef}
            parameterList={parameters}
            handleCheckedItem={handleCheckedItem}
            handleSelectAll={handleSelectAll}
            fieldHeight={fieldHeight}
            languageStrings={languageStrings}
            isSelectAll={isSelectAll}
            isOpenSelectList={isOpenSelectList}
            wrapperRef={wrapperRef}
            selectedIndexOfPeriod={selectedIndexOfPeriod}
            scrollTo={popUpScrollTo}
            scrollToWithCustomPeriod={popUpScrollToWithCustomPeriod}
            handleTransformName={handleTransformName}
            title={title}
          />
        )}
        {paramsIsUploading && <StyledLoader color={primary} />}
      </FieldWrapper>
      {isShowMessage && (
        <Text type={TextType.TEXT_12_GRAY} color={red2} margin={'4px 0 0 12px'}>
          {title ? `${languageStrings.generateReportModalShowMessage} ${title.toLocaleLowerCase()}` : ``}
        </Text>
      )}
    </InputWrapper>
  );
};
